import { useEffect, useRef, useState } from "react";
import { InstallDialog } from "@/components/InstallDialog";
import {
  Github,
  Search,
  Loader2,
  Download,
  AlertTriangle,
  Star,
  Code2,
  Scale,
  Boxes,
  Terminal,
  Sparkles,
  Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/pages/Dashboard";
import { toast } from "sonner";
import { analyzeRepo, CAP_LABEL, HF_REPO_RE } from "@/lib/repos";
import { installAgentModel, stopInstall } from "@/lib/agent";
import { listSavedRepos, saveRepo, deleteSavedRepo } from "@/lib/api";
import { SavedRepos } from "@/components/SavedRepos";
import { useAppConfig } from "@/context/ConfigContext";

const RUNTIMES = [
  ["mflux", "mflux · Imagen MLX (FLUX)"],
  ["mlx-gen", "mlx-gen · Imagen MLX (Qwen Edit)"],
  ["ltx-2-mlx", "LTX-2 MLX · Vídeo"],
  ["sensenova-swift", "SenseNova Swift · Edición"],
  ["llama.cpp", "llama.cpp · GGUF"],
];

const CAP_STYLE = {
  text: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  agent: "border-violet-500/40 bg-violet-500/10 text-violet-300",
  image: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  video: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  audio: "border-pink-500/40 bg-pink-500/10 text-pink-300",
};

function CapBadge({ cap }) {
  return (
    <span className={`rounded-sm border px-2 py-0.5 font-mono text-[10px] ${CAP_STYLE[cap] || "border-[#404040] text-[#d4d4d4]"}`}>
      {CAP_LABEL[cap] || cap}
    </span>
  );
}

export default function Repositories() {
  const { refreshModels } = useAppConfig();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [rows, setRows] = useState([]); // modelos HF con runtime editable + estado
  const [installingId, setInstallingId] = useState(null);
  const [pendingRow, setPendingRow] = useState(null);
  const abortRef = useRef(null);
  const stoppedRef = useRef(false);
  const [startedAt, setStartedAt] = useState(null);
  const [, setTick] = useState(0);
  const [saved, setSaved] = useState([]);

  const loadSaved = () => listSavedRepos().then(setSaved).catch(() => {});
  useEffect(() => { loadSaved(); }, []);

  const openSaved = (r) => {
    setUrl(r.url);
    setError(null);
    setResult(r.result);
    setRows((r.rows || []).map((m) => ({ ...m, status: "idle", error: null })));
  };

  const removeSaved = async (r) => {
    if (!window.confirm(`¿Quitar ${r.owner}/${r.repo} de la lista? (no borra modelos instalados)`)) return;
    await deleteSavedRepo(r.url).catch(() => toast.error("No se pudo eliminar"));
    loadSaved();
  };

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const elapsed = () => {
    const s = Math.floor((Date.now() - (startedAt || Date.now())) / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const analyze = async () => {
    if (!url.trim() || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setRows([]);
    try {
      const data = await analyzeRepo(url.trim());
      const newRows = data.installable.map((m, i) => ({ ...m, key: `${m.repo_id || m.name}-${i}`, status: "idle" }));
      setResult(data);
      setRows(newRows);
      saveRepo({ url: data.url, owner: data.owner, repo: data.repo, result: data, rows: newRows })
        .then(loadSaved)
        .catch(() => toast.warning("No se pudo guardar el repositorio en la lista"));
      if (data.installable.length === 0 && data.families.length === 0) {
        toast.message("Análisis completado: no se detectaron modelos conocidos en el README.");
      }
    } catch (e) {
      setError(e?.message || "No se pudo analizar el repositorio.");
    } finally {
      setLoading(false);
    }
  };

  const setRow = (key, patch) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const askInstall = (row) => {
    if (installingId) return;
    if (!HF_REPO_RE.test(row.repo_id || "")) {
      toast.error("Indica un repositorio de Hugging Face válido: usuario/modelo");
      return;
    }
    setPendingRow(row);
  };

  const install = async (row, modelsDir) => {
    setPendingRow(null);
    setInstallingId(row.key);
    setStartedAt(Date.now());
    setRow(row.key, { status: "installing", error: null, dir: modelsDir || null });
    const payload = {
      name: row.name,
      repo_id: row.repo_id,
      runtime: row.runtime,
      type: row.type,
      capabilities: row.capabilities,
    };
    if (row.include) payload.include = row.include;
    if (modelsDir) payload.models_dir = modelsDir;
    abortRef.current = new AbortController();
    stoppedRef.current = false;
    const d = await installAgentModel(payload, abortRef.current.signal).catch((e) => ({ ok: false, error: e?.message || "Error de red" }));
    if (stoppedRef.current) return;
    if (d?.ok) {
      setRow(row.key, { status: "done", installedPath: d?.model?.local_path });
      toast.success(`Modelo instalado: ${row.name}`);
      await refreshModels();
    } else {
      setRow(row.key, { status: "error", error: d?.error });
      toast.error(d?.error || "No se pudo instalar el modelo");
    }
    setInstallingId(null);
    setStartedAt(null);
  };

  const stop = async (row) => {
    if (!window.confirm("¿Detener la instalación de este modelo?")) return;
    stoppedRef.current = true;
    setRow(row.key, { status: "idle" });
    setInstallingId(null);
    setStartedAt(null);
    const d = await stopInstall(abortRef.current, { repo_id: row.repo_id, name: row.name });
    if (d?.ok) toast.success(d.message || "Instalación detenida");
    else toast.warning(d?.error);
  };

  return (
    <div className="krai-enter">
      <PageHeader
        title="Repositorios"
        subtitle="Analiza un repo de GitHub, detecta sus modelos e instálalos en tu Mac con un clic"
      />

      {/* Entrada de URL */}
      <div className="rounded-md border border-[#262626] bg-[#121212] p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Github className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#737373]" />
            <Input
              data-testid="repo-url-input"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && analyze()}
              placeholder="https://github.com/usuario/repositorio"
              className="border-[#262626] bg-[#0a0a0a] pl-9 font-mono text-xs focus-visible:ring-1 focus-visible:ring-neutral-500"
            />
          </div>
          <Button
            data-testid="repo-analyze-btn"
            onClick={analyze}
            disabled={!url.trim() || loading}
            className="rounded-sm bg-white text-black hover:bg-neutral-200"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            {loading ? "Analizando…" : "Analizar"}
          </Button>
        </div>
        <p className="mt-2 font-mono text-[11px] text-[#737373]">
          El análisis se hace leyendo el repo en GitHub. La instalación la ejecuta el Agent en tu Mac (no sube nada a KRAI).
        </p>
        <SavedRepos items={saved} activeUrl={result?.url} onOpen={openSaved} onDelete={removeSaved} />
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-red-500/40 bg-red-500/10 p-4" data-testid="repo-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
          <div className="font-mono text-sm text-red-200">{error}</div>
        </div>
      )}

      {result && (
        <div className="mt-4 space-y-4" data-testid="repo-result">
          {/* Cabecera del repo */}
          <div className="rounded-md border border-[#262626] bg-[#121212] p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <a
                  href={result.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-heading text-lg font-semibold text-white hover:underline"
                  data-testid="repo-title"
                >
                  {result.owner}/{result.repo}
                </a>
                {result.meta.description && (
                  <p className="mt-1 max-w-2xl text-sm text-[#a3a3a3]">{result.meta.description}</p>
                )}
              </div>
              <span className="shrink-0 rounded-sm border border-[#404040] bg-[#0a0a0a] px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#a3a3a3]">
                {result.category}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 font-mono text-[11px] text-[#737373]">
              <span className="inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> {result.meta.stars}</span>
              <span className="inline-flex items-center gap-1.5"><Code2 className="h-3.5 w-3.5" /> {result.meta.language}</span>
              <span className="inline-flex items-center gap-1.5"><Scale className="h-3.5 w-3.5" /> {result.meta.license}</span>
            </div>
            {result.families.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#737373]">Familias detectadas:</span>
                {result.families.map((f) => (
                  <CapBadge key={f.name} cap={f.capability} />
                ))}
              </div>
            )}
          </div>

          {/* Modelos instalables (Hugging Face) */}
          <div className="rounded-md border border-[#262626] bg-[#121212] p-4">
            <div className="flex items-center gap-2 text-[#737373]">
              <Boxes className="h-4 w-4" />
              <span className="font-mono text-[11px] uppercase tracking-widest">
                Modelos instalables · {rows.length}
              </span>
            </div>

            {rows.length === 0 ? (
              <div className="mt-4 rounded-sm border border-[#262626] bg-[#0a0a0a] p-6 text-center">
                <Sparkles className="mx-auto h-6 w-6 text-[#525252]" />
                <p className="mx-auto mt-3 max-w-md text-sm text-[#a3a3a3]">
                  No se detectaron modelos conocidos en este repo. Si es una app completa (como WanGP), revisa el plan de
                  instalación de abajo.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-3" data-testid="repo-models-list">
                {rows.map((row) => {
                  const valid = HF_REPO_RE.test(row.repo_id || "");
                  const locked = row.status === "installing" || row.status === "done";
                  return (
                    <div
                      key={row.key}
                      data-testid={`repo-model-${row.key}`}
                      className="rounded-sm border border-[#262626] bg-[#0a0a0a] p-3"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-[#f5f5f5]">{row.name}</span>
                            <CapBadge cap={row.capability || "image"} />
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {row.capabilities.map((c) => (
                              <span key={c} className="rounded-sm border border-[#404040] bg-[#121212] px-1.5 py-0.5 font-mono text-[10px] text-[#d4d4d4]">
                                {c}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={row.runtime}
                            onValueChange={(v) => setRow(row.key, { runtime: v })}
                            disabled={locked}
                          >
                            <SelectTrigger
                              data-testid={`repo-runtime-${row.key}`}
                              className="h-9 w-[210px] border-[#262626] bg-[#121212] font-mono text-[11px]"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="border-[#262626] bg-[#121212]">
                              {RUNTIMES.map(([id, label]) => (
                                <SelectItem key={id} value={id} className="font-mono text-[11px]">
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            data-testid={`repo-install-${row.key}`}
                            onClick={() => askInstall(row)}
                            disabled={!!installingId || row.status === "done" || !valid}
                            className="rounded-sm bg-white text-black hover:bg-neutral-200 disabled:opacity-60"
                          >
                            {row.status === "installing" ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Instalando…</>
                            ) : row.status === "done" ? (
                              "Instalado"
                            ) : (
                              <><Download className="mr-2 h-4 w-4" />Instalar</>
                            )}
                          </Button>
                          {row.status === "installing" && (
                            <Button
                              data-testid={`repo-stop-${row.key}`}
                              onClick={() => stop(row)}
                              className="rounded-sm border border-red-500/50 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                            >
                              <Square className="mr-2 h-3.5 w-3.5" />Detener
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="mt-2.5">
                        <label className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-[#737373]">
                          Repositorio Hugging Face
                        </label>
                        <Input
                          data-testid={`repo-repoid-${row.key}`}
                          value={row.repo_id}
                          onChange={(e) => setRow(row.key, { repo_id: e.target.value.trim() })}
                          disabled={locked}
                          placeholder="usuario/modelo"
                          className={`h-9 border-[#262626] bg-[#121212] font-mono text-[11px] ${
                            row.repo_id && !valid ? "border-red-500/60" : ""
                          }`}
                        />
                        {row.repo_id && !valid && (
                          <p className="mt-1 font-mono text-[10px] text-red-400">Formato: usuario/modelo</p>
                        )}
                        {row.status === "installing" && (
                          <p className="mt-1 font-mono text-[10px] text-emerald-400" data-testid={`repo-progress-${row.key}`}>
                            Descargando en tu Mac · {elapsed()} · destino: {row.dir || "carpeta por defecto del Agent"}. No cierres esta pestaña.
                          </p>
                        )}
                        {row.status === "done" && row.installedPath && (
                          <p className="mt-1 break-all font-mono text-[10px] text-emerald-400" data-testid={`repo-installed-path-${row.key}`}>
                            Instalado en: {row.installedPath}
                          </p>
                        )}
                        {row.status === "error" && row.error && (
                          <p className="mt-1 break-all font-mono text-[10px] text-red-400">{row.error}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Plan de instalación (apps que no son modelos HF) */}
          {(result.installPlan.clone || result.installPlan.deps || result.installPlan.run) && (
            <div className="rounded-md border border-[#262626] bg-[#121212] p-4" data-testid="repo-install-plan">
              <div className="flex items-center gap-2 text-[#737373]">
                <Terminal className="h-4 w-4" />
                <span className="font-mono text-[11px] uppercase tracking-widest">Plan de instalación (app completa)</span>
              </div>
              <pre className="mt-3 overflow-x-auto rounded-sm border border-[#262626] bg-[#0a0a0a] p-3 font-mono text-[11px] leading-relaxed text-[#d4d4d4]">
{[result.installPlan.clone, `cd ${result.repo}`, result.installPlan.env, result.installPlan.deps, result.installPlan.run]
  .filter(Boolean)
  .join("\n")}
              </pre>
              <p className="mt-2 font-mono text-[11px] text-amber-400">
                Nota: además de los modelos de arriba, este repo es una app completa (p.ej. WanGP) que requiere entorno
                Python y GPU. Usa este plan para clonarla y ejecutarla manualmente en tu Mac.
              </p>
            </div>
          )}
        </div>
      )}
      <InstallDialog row={pendingRow} onCancel={() => setPendingRow(null)} onConfirm={(dir) => install(pendingRow, dir)} />
    </div>
  );
}
